import type {
  CSSProperties,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactNode,
  RefObject,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import { useFieldContext } from './Field'
import { FIELD_SHADOW, FOCUS_FIELD, FOCUS_FIELD_INVALID } from './control-styles'
import { IconCheck, IconChevronDown, IconSearch } from './icons'
import { Keycap } from './Keycap'
import {
  MENU_EDGE,
  MENU_GAP,
  type MenuAnchor,
  type MenuCloseCause,
  type MenuPlacement,
  type MenuSpot,
  type MenuViewport,
  nextMenuIndex,
  placeMenu,
  returnsFocusToTrigger,
} from './Menu'

/*
 * Every bordered field shares one face: the surface fill, a `border-strong`
 * hairline, an 8px corner, the extra-small shadow and 14/20 type. There is no
 * hover change, because Untitled draws none and a field that brightens under
 * the pointer reads as a button.
 */
const CONTROL =
  'w-full rounded-lg border bg-surface text-base text-ink placeholder:text-ink-subtle transition ' +
  FIELD_SHADOW +
  ' disabled:cursor-not-allowed disabled:border-line-strong disabled:bg-surface-muted disabled:text-ink-subtle'

const VALID = 'border-line-strong ' + FOCUS_FIELD

/**
 * The filled face: no edge and the selected-grey ground, no shadow. The
 * reference draws a picker sitting in a table row this way, so a column of
 * them reads as values rather than as a stack of empty boxes. Focus brings
 * the edge back.
 */
const CONTROL_FILLED =
  'w-full rounded-lg border bg-surface-selected text-base text-ink placeholder:text-ink-subtle transition shadow-none' +
  ' disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle'

const VALID_FILLED = 'border-transparent ' + FOCUS_FIELD
/*
 * The red edge at rest is Untitled's error 300, which is softer than the focus
 * red so a wrong field does not shout before anybody is in it.
 */
const INVALID = 'border-error-300 ' + FOCUS_FIELD_INVALID

/** A read-only field looks like any other and gives no sign it could be typed in. */
const READ_ONLY = 'focus:outline-none caret-transparent'

export type InputSize = 'xs' | 'sm' | 'md'

/** 32 for the builder name chip, 36 for toolbars and popovers, 40 for forms. */
const HEIGHTS: Record<InputSize, string> = {
  xs: 'h-8 px-2.5',
  sm: 'h-9 px-3',
  md: 'h-10 px-3.5',
}

/** The text indent that clears a 20px leading icon at its inset. */
const LEADING_INDENT: Record<InputSize, string> = {
  xs: 'pl-9',
  sm: 'pl-10',
  md: 'pl-[42px]',
}

const LEADING_INSET: Record<InputSize, string> = {
  xs: 'left-2.5',
  sm: 'left-3',
  md: 'left-3.5',
}

/*
 * The dashed face is the builder's, where a name sits in the header and looks
 * like a label until somebody clicks it. `chip` is the campaign name at 32 in
 * the 16px type, `true` is a variant's name at 40.
 */
const DASHED: Record<'chip' | 'variant', string> = {
  chip: 'border-dashed border-primary-300 text-title font-semibold',
  variant: 'border-dashed border-primary-500 font-medium',
}

/** The filled face is a select that sits inside a row, such as a role picker. */
const FILLED = 'border-transparent bg-surface-selected shadow-none'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  invalid?: boolean
  /** Small element inside the left edge, usually an icon. */
  leading?: ReactNode
  /** Small element inside the right edge, usually a unit, a status word or a key cap. */
  trailing?: ReactNode
  inputSize?: InputSize
  /**
   * A fixed segment joined to the left edge, such as `https://`. It takes the
   * field's left corners, so the two read as one control.
   */
  addon?: ReactNode
  /** The builder's dashed name field. `'chip'` is the 32px header chip. */
  dashed?: boolean | 'chip' | 'variant'
  /** No border and the selected grey, for a field inside a row. */
  filled?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, leading, trailing, inputSize = 'md', addon, dashed, filled, id, ...rest },
  ref,
) {
  const field = useFieldContext()
  const isInvalid = invalid ?? field?.invalid ?? false
  const dashedFace = dashed === true ? 'variant' : dashed === false ? undefined : dashed
  const size: InputSize = dashedFace === 'chip' ? 'xs' : inputSize

  const input = (
    <input
      ref={ref}
      id={id ?? field?.id}
      aria-describedby={field?.describedBy}
      aria-invalid={isInvalid || undefined}
      required={field?.required}
      disabled={rest.disabled ?? field?.disabled}
      className={cn(
        CONTROL,
        isInvalid ? INVALID : VALID,
        rest.readOnly && READ_ONLY,
        HEIGHTS[size],
        dashedFace && DASHED[dashedFace],
        filled && FILLED,
        leading && LEADING_INDENT[size],
        trailing && 'pr-10',
        addon && 'min-w-0 flex-1 rounded-l-none',
        className,
      )}
      {...rest}
    />
  )

  const withSlots =
    !leading && !trailing ? (
      input
    ) : (
      <div className={cn('relative', addon && 'min-w-0 flex-1')}>
        {leading ? (
          <span
            className={cn(
              'pointer-events-none absolute inset-y-0 grid place-items-center text-ink-subtle [&>svg]:h-5 [&>svg]:w-5',
              LEADING_INSET[size],
            )}
          >
            {leading}
          </span>
        ) : null}
        {input}
        {trailing ? (
          <span className="absolute inset-y-0 right-2.5 grid place-items-center text-ink-subtle">{trailing}</span>
        ) : null}
      </div>
    )

  if (!addon) return withSlots

  return (
    <div className="flex w-full items-stretch">
      <span
        className={cn(
          'inline-flex shrink-0 items-center rounded-l-lg border border-r-0 border-line-strong bg-surface px-3 text-base text-ink-muted',
          FIELD_SHADOW,
        )}
      >
        {addon}
      </span>
      {withSlots}
    </div>
  )
})

export interface SearchInputProps extends InputProps {
  /**
   * The key that focuses this field, drawn as a small cap inside the right
   * edge. The page still has to bind the key: this only says what it is.
   */
  shortcut?: string
}

/**
 * A search field: the ordinary field with a search glyph, at 36 in toolbars
 * and popovers.
 *
 * It used to be drawn sunken, with no edge, as a well cut into the list. The
 * measured design draws it as a normal field, and a filter bar whose search
 * looks like every other field is one fewer thing to learn.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { className, shortcut, trailing, inputSize = 'sm', ...rest },
  ref,
) {
  return (
    <Input
      ref={ref}
      type="search"
      inputSize={inputSize}
      leading={<IconSearch />}
      trailing={trailing ?? (shortcut ? <Keycap size="inline">{shortcut}</Keycap> : undefined)}
      className={className}
      {...rest}
    />
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
  /**
   * Draw the `0/300` counter inside the box. On by default whenever
   * `maxLength` is set, because a limit nobody can see is a limit people
   * discover by losing the end of what they typed.
   */
  counter?: boolean
}

/** The counter's words, so the arithmetic is testable apart from the markup. */
export function textareaCount(length: number, max: number): { text: string; atLimit: boolean } {
  return { text: `${length}/${max}`, atLimit: length >= max }
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, id, rows = 5, counter, onChange, ...rest },
  ref,
) {
  const field = useFieldContext()
  const isInvalid = invalid ?? field?.invalid ?? false
  // Uncontrolled fields still count, from what was typed. A controlled field
  // counts its prop, so the number can never disagree with the text.
  const [typed, setTyped] = useState(() => String(rest.defaultValue ?? '').length)
  const length = rest.value !== undefined && rest.value !== null ? String(rest.value).length : typed
  const max = rest.maxLength
  const showCounter = max !== undefined && max > 0 && counter !== false
  const count = showCounter ? textareaCount(length, max) : null

  const area = (
    <textarea
      ref={ref}
      id={id ?? field?.id}
      rows={rows}
      aria-describedby={field?.describedBy}
      aria-invalid={isInvalid || undefined}
      required={field?.required}
      disabled={rest.disabled ?? field?.disabled}
      onChange={(event) => {
        setTyped(event.currentTarget.value.length)
        onChange?.(event)
      }}
      className={cn(
        CONTROL,
        isInvalid ? INVALID : VALID,
        rest.readOnly && READ_ONLY,
        'block min-h-32 resize-y px-3.5 py-3',
        count && 'pb-8',
        className,
      )}
      {...rest}
    />
  )

  if (!count) return area

  return (
    <div className="relative">
      {area}
      <span
        aria-hidden="true"
        data-numeric=""
        className={cn(
          'pointer-events-none absolute bottom-2.5 right-3.5 text-xs tabular-nums',
          count.atLimit ? 'text-danger-text' : 'text-ink-subtle',
        )}
      >
        {count.text}
      </span>
    </div>
  )
})

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  /** Supporting text after the label, in the subtle ink: `Workdays (Monday - Friday)`. */
  description?: string
  /** A count at the right edge of the row. Drawn, never part of the value. */
  count?: number | string
  /** Why the row cannot be chosen. Said on the row, not only in a tooltip. */
  disabledReason?: string
  /** The heading this row sits under. `<optgroup>` children fill it in. */
  group?: string
  /** A 20px glyph or flag before the label. */
  icon?: ReactNode
}

/** A row at the foot of the list that does something instead of choosing, such as creating. */
export interface SelectFooterAction {
  label: ReactNode
  onSelect: () => void
  icon?: ReactNode
}

export type SelectVariant = 'outline' | 'filled'

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  invalid?: boolean
  options?: SelectOption[]
  /** Shown as the first, empty option. Leave it out to require a choice. */
  placeholder?: string
  selectSize?: 'sm' | 'md'
  /**
   * `outline` is the ordinary field. `filled` drops the edge and the shadow
   * and sits on the selected-grey ground, for a picker inside a table row.
   */
  variant?: SelectVariant
  /** A 20px glyph or flag inside the trigger's left edge. */
  leading?: ReactNode
  /**
   * A search row at the top of the list. On by default once there are more
   * than eight rows, which is where scanning stops being quicker than typing.
   */
  searchable?: boolean
  /** `+ Create new product` and the like, under a rule at the foot of the list. */
  footerAction?: SelectFooterAction
  /** A line under the trigger that explains the current choice, and changes with it. */
  optionDescription?: (option: SelectOption | undefined) => ReactNode
  /**
   * Several values at once. Each row gets a checkbox, the list stays open while
   * rows are ticked, and the trigger says `3 selected`.
   */
  multiple?: boolean
  /** The chosen values when `multiple` is set and the caller owns them. */
  values?: readonly string[]
  /** The starting values when `multiple` is set and the control owns them. */
  defaultValues?: readonly string[]
  onValuesChange?: (values: string[]) => void
}

/*
 * ---------------------------------------------------------------------------
 * The dropdown the app draws itself
 * ---------------------------------------------------------------------------
 *
 * WHY THERE IS STILL A `<select>` UNDER THIS. Ninety-eight call sites pass an
 * `onChange` that reads `event.target.value` or `event.currentTarget.value`,
 * some pass `required`, some sit in a real form, and the component forwards its
 * ref as an `HTMLSelectElement`. A component that replaced the element with a
 * button and invented an event shape would have to be threaded through every
 * one of them. So the native control stays exactly where it was, carrying the
 * name, the value, the form participation and the ref, and it is driven from
 * the custom list: assign the value through the prototype setter, dispatch a
 * real bubbling `change`, and React delivers the same event it always did. The
 * painted list is a presentation layer over a real control rather than a
 * replacement for it, and no call site changed.
 *
 * WHY THE NATIVE ELEMENT IS TRANSPARENT RATHER THAN `sr-only`. It keeps the
 * trigger's box, at `opacity-0` and `pointer-events-none`. An element clipped
 * to a pixel is still focusable, but a `required` control that the browser
 * cannot point at blocks a native submit with a bubble nobody can see. Left at
 * full size the bubble lands on the control the person is looking at. It takes
 * `tabIndex={-1}` and `aria-hidden` so there is one tab stop and one thing for
 * a screen reader to read, and a programmatic `focus()` through the forwarded
 * ref is handed on to the visible trigger.
 *
 * WHY THE PANEL IS PORTALLED AND PLACED BY ARITHMETIC. The same reason `Menu`
 * is, and this file reuses that engine rather than growing a second one: a
 * panel positioned inside the layout composites in whatever stacking context
 * its ancestors make, and this app puts selects inside dialogs, drawers and a
 * scrolling table, which is the likeliest way a dropdown looks broken. The one
 * thing added here is the flip: `placeMenu` never changes side, because both
 * menus that use it sit at a fixed end of the sidebar, while a select can sit
 * anywhere down a long form.
 *
 * WHY THE HIGHLIGHT IS `aria-activedescendant` AND NOT REAL FOCUS. `Menu` moves
 * focus onto the row, which it can because it opens from the chrome. A select
 * opens from inside a dialog, and focus leaving the dialog for a panel in the
 * body is exactly what a focus trap is written to undo. Focus stays on the
 * trigger for the whole interaction, so nothing else has to know the list is
 * there.
 */

/** How tall one row is drawn, near enough to decide which way the list opens. */
export const SELECT_ROW_HEIGHT = 36

/** The list's own padding, top and bottom together. Four on each side. */
export const SELECT_LIST_PAD = 8

/** A long list scrolls rather than filling the window. */
export const SELECT_MAX_HEIGHT = 320

/** A narrow trigger still gets a panel wide enough to read a label in. */
export const SELECT_MIN_WIDTH = 240

/** How long typed letters keep adding to one word before they start a new one. */
export const SELECT_TYPEAHEAD_MS = 800

/** Above this many rows the list grows a search field without being asked. */
export const SELECT_SEARCH_AT = 8

/** The search row or the footer row: a 36px control, 4 of padding each side, a 1px rule. */
export const SELECT_EXTRA_ROW = 45

/**
 * The height the list would like, before the window gets a say.
 *
 * `extra` is the search row and the footer, which sit outside the scrolling
 * list and so are never cut by the cap.
 */
export function selectPopupHeight(count: number, extra = 0): number {
  const wanted = Math.max(count, 1) * SELECT_ROW_HEIGHT + SELECT_LIST_PAD
  return Math.min(SELECT_MAX_HEIGHT, wanted) + extra
}

/** Whether a row survives what was typed in the search row. Case does not matter. */
export function selectMatches(option: SelectOption, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === '') return true
  return option.label.toLowerCase().includes(needle)
}

/**
 * The rows the keys walk while a search is typed.
 *
 * A row the search hides is marked disabled rather than removed, so every
 * index still means the same row and `nextMenuIndex` skips it for free.
 */
export function searchableItems(items: readonly SelectOption[], query: string): SelectOption[] {
  if (query.trim() === '') return [...items]
  return items.map((item) => (selectMatches(item, query) ? item : { ...item, disabled: true }))
}

/** What the trigger says when several rows can be chosen. */
export function multiSelectLabel(chosen: readonly SelectOption[], placeholder: string | undefined): string {
  if (chosen.length === 0) return placeholder ?? ''
  if (chosen.length === 1) return (chosen[0] as SelectOption).label
  return `${chosen.length} selected`
}

/** The values after a row is ticked or unticked, kept in the list's own order. */
export function toggleSelectValue(
  items: readonly SelectOption[],
  current: readonly string[],
  value: string,
): string[] {
  const next = new Set(current)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return items.map((item) => item.value).filter((candidate) => next.has(candidate))
}

/**
 * Which side of the trigger the list opens on.
 *
 * Below unless below cannot hold it and above holds more, which is the rule
 * that keeps a select near the foot of a dialog from opening into four pixels
 * of nothing. It is deliberately not "whichever side has more room": a list
 * that fits below belongs below, or the same control would open one way on a
 * tall window and the other way on a short one.
 */
export function selectPlacement(
  anchor: MenuAnchor,
  viewport: MenuViewport,
  wanted: number,
): MenuPlacement {
  const below = viewport.height - anchor.bottom - MENU_GAP - MENU_EDGE
  const above = anchor.top - MENU_GAP - MENU_EDGE
  return below < wanted && above > below ? 'top' : 'bottom'
}

/** Where the panel goes: `placeMenu`'s arithmetic, plus the flip and the cap. */
export function placeSelect(
  anchor: MenuAnchor & { width: number },
  viewport: MenuViewport,
  count: number,
  extra = 0,
): MenuSpot {
  const wanted = selectPopupHeight(count, extra)
  const spot = placeMenu(anchor, {
    placement: selectPlacement(anchor, viewport, wanted),
    align: 'start',
    width: Math.max(anchor.width, SELECT_MIN_WIDTH),
    viewport,
  })
  return { ...spot, maxHeight: Math.min(spot.maxHeight, wanted) }
}

/** The text of a child, for an `<option>` that spells its label out in JSX. */
function textOf(node: ReactNode): string {
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map((part) => textOf(part as ReactNode)).join('')
  if (isValidElement(node)) return textOf((node.props as { children?: ReactNode }).children)
  return ''
}

function collectOptions(children: ReactNode, into: SelectOption[], group?: string): void {
  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue
    const props = child.props as { value?: unknown; disabled?: boolean; children?: ReactNode; label?: string }
    if (child.type === 'option') {
      const label = textOf(props.children)
      const value = props.value === undefined ? label : String(props.value)
      const option: SelectOption = props.disabled === true ? { value, label, disabled: true } : { value, label }
      if (group !== undefined) option.group = group
      into.push(option)
      continue
    }
    // A group's heading is not a row and cannot be chosen, so the walk sees the
    // options inside it, and each one remembers the heading it sat under.
    if (child.type === 'optgroup') collectOptions(props.children, into, props.label)
  }
}

/**
 * The rows, from both ways of declaring them.
 *
 * Half the call sites pass `options` and half write `<option>` children, and a
 * few do both, so the list the custom panel draws is built the same way the
 * browser builds the native one: the placeholder first, then the prop, then the
 * children in source order.
 */
export function selectItemsFrom(
  options: readonly SelectOption[] | undefined,
  children: ReactNode,
  placeholder: string | undefined,
): SelectOption[] {
  const items: SelectOption[] = []
  if (placeholder !== undefined) items.push({ value: '', label: placeholder })
  for (const option of options ?? []) items.push(option)
  collectOptions(children, items)
  return items
}

/** What the control shows before anybody touches it, native rules. */
export function initialSelectValue(
  defaultValue: SelectProps['defaultValue'],
  items: readonly SelectOption[],
): string {
  if (defaultValue !== undefined) return selectValueOf(defaultValue)
  return items[0]?.value ?? ''
}

/** The `value` prop as the DOM would hold it. */
export function selectValueOf(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.length === 0 ? '' : String(value[0])
  return String(value)
}

/**
 * Where typing lands.
 *
 * One letter steps to the next row starting with it, so pressing it again walks
 * the rows rather than sticking on the first. More letters match the whole word
 * from where the highlight already is, so extending what you typed does not
 * jump off the row you were narrowing down to. A letter held down repeats into
 * the same buffer, and repeating one letter is treated as pressing it again.
 */
export function typeaheadIndex(
  items: readonly SelectOption[],
  typed: string,
  from: number,
): number {
  const needle = typed.toLowerCase()
  if (needle === '') return -1
  const oneLetter = needle.split('').every((letter) => letter === needle[0])
  const match = oneLetter ? (needle[0] as string) : needle
  // One letter steps past the row it is already on. A longer word is measured
  // from that row, so narrowing what you typed does not jump off it.
  const begin = oneLetter ? from + 1 : Math.max(from, 0)
  for (let step = 0; step < items.length; step += 1) {
    const index = (((begin + step) % items.length) + items.length) % items.length
    const item = items[index] as SelectOption
    if (item.disabled === true) continue
    if (item.label.toLowerCase().startsWith(match)) return index
  }
  return -1
}

/** What a key does. The component applies it, so the rules can be tested. */
export type SelectAction =
  | { kind: 'none' }
  | { kind: 'open'; active: number }
  | { kind: 'close'; cause: MenuCloseCause }
  | { kind: 'active'; active: number }
  | { kind: 'choose'; index: number }
  | { kind: 'type'; typed: string; index: number }
  | { kind: 'search'; query: string }

export interface SelectKeyState {
  open: boolean
  /** The highlighted row while the list is open, the chosen one while it is shut. */
  active: number
  /** Letters typed inside the typeahead window, empty once it has lapsed. */
  typed: string
  /** The list has a search row, so letters typed while it is open go there. */
  searchable?: boolean
  /** What the search row holds. */
  query?: string
}

/** Where the highlight starts when the list opens: on the chosen row. */
function openingActive(items: readonly SelectOption[], selected: number): number {
  const item = items[selected]
  if (item !== undefined && item.disabled !== true) return selected
  return nextMenuIndex(items, -1, 'ArrowDown')
}

export function selectKeyAction(
  key: string,
  state: SelectKeyState,
  items: readonly SelectOption[],
): SelectAction {
  const { open, active, typed } = state

  if (key === 'Escape') return open ? { kind: 'close', cause: 'escape' } : { kind: 'none' }
  if (key === 'Tab') return open ? { kind: 'close', cause: 'tab' } : { kind: 'none' }

  if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End') {
    if (!open) return { kind: 'open', active: openingActive(items, active) }
    return { kind: 'active', active: nextMenuIndex(items, active, key) }
  }

  // With a search row, an open list sends letters to the search rather than
  // to typeahead. A shut list still moves the value as a native select does.
  if (open && state.searchable === true) {
    const query = state.query ?? ''
    if (key === 'Backspace') return query === '' ? { kind: 'none' } : { kind: 'search', query: query.slice(0, -1) }
    if (key === ' ' && query !== '') return { kind: 'search', query: query + ' ' }
    if (key.length === 1 && key !== ' ') return { kind: 'search', query: query + key }
  }

  if (key === 'Enter' || key === ' ') {
    if (!open) return { kind: 'open', active: openingActive(items, active) }
    // A space inside a word being typed is part of the word, not a choice.
    if (key === ' ' && typed !== '') return typeAction(items, typed + ' ', active)
    return { kind: 'choose', index: active }
  }

  if (key.length === 1) return typeAction(items, typed + key, active)
  return { kind: 'none' }
}

function typeAction(items: readonly SelectOption[], typed: string, from: number): SelectAction {
  return { kind: 'type', typed, index: typeaheadIndex(items, typed, from) }
}

/** The three things the painted list needs from the real control. */
export interface NativeSelect {
  value: string
  assign: (next: string) => void
  fire: () => void
}

/**
 * Hand a choice to the native element, which is what fires the caller's
 * `onChange`.
 *
 * Nothing happens for a row that cannot be chosen, and nothing happens when the
 * row already chosen is chosen again, because a native select fires no change
 * event for that either and a call site that saves on every change would write
 * a row for a click that changed nothing.
 */
export function commitSelectValue(target: NativeSelect, option: SelectOption | undefined): boolean {
  if (option === undefined) return false
  if (option.disabled === true) return false
  if (option.value === target.value) return false
  target.assign(option.value)
  target.fire()
  return true
}

/*
 * The setter on the prototype rather than `node.value = next`.
 *
 * React puts an own `value` property on the form controls it renders, to track
 * what the element held last. Assigning through the prototype leaves that
 * bookkeeping alone and is the one arrangement that works whichever way React
 * decides to read a change for this element type. It is read once, and guarded,
 * because this module is also rendered to a string under test where there is no
 * DOM at all.
 */
const NATIVE_VALUE: ((this: HTMLSelectElement, value: string) => void) | undefined =
  typeof HTMLSelectElement === 'undefined'
    ? undefined
    : Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set

function nativeTarget(node: HTMLSelectElement): NativeSelect {
  return {
    value: node.value,
    assign: (next) => {
      if (NATIVE_VALUE === undefined) node.value = next
      else NATIVE_VALUE.call(node, next)
    },
    fire: () => node.dispatchEvent(new Event('change', { bubbles: true })),
  }
}

/** A row's id, which is what `aria-activedescendant` on the trigger points at. */
export function selectOptionId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`
}

export interface SelectListboxProps {
  id: string
  /** The trigger, which carries the field's label. */
  labelledBy: string
  items: readonly SelectOption[]
  /** The chosen row, which wears the tick. */
  selected: number
  /** The highlighted row, which the arrow keys move. */
  active: number
  spot: MenuSpot
  onChoose?: (index: number) => void
  onActivate?: (index: number) => void
  elementRef?: RefObject<HTMLDivElement>
  registerRow?: (index: number, node: HTMLLIElement | null) => void
  /** Several rows can be chosen, each with its own checkbox. */
  multiple?: boolean
  /** The chosen rows when `multiple` is set. */
  chosen?: ReadonlySet<number>
  /** Draw the search row, holding this text. */
  query?: string
  searchable?: boolean
  /** The row at the foot. Its index is `items.length`, so the keys reach it too. */
  footerAction?: SelectFooterAction
}

/** The small box a multi-select row wears, drawn rather than a real input inside an option. */
function RowCheck({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid h-4 w-4 shrink-0 place-items-center rounded border',
        checked ? 'border-primary-600 bg-primary-600 text-white' : 'border-line-strong bg-surface',
      )}
    >
      {checked ? <IconCheck className="h-3 w-3" strokeWidth={2} /> : null}
    </span>
  )
}

/**
 * The panel, separated from the wiring so it can be rendered on its own.
 *
 * It wears the surface `MenuPanel` wears: the surface fill, one hairline, the
 * 8px corner and the large shadow. Rows are 36 tall and rounded inside the 4px
 * padding, which is the measured filter and label row.
 */
export function SelectListbox({
  id,
  labelledBy,
  items,
  selected,
  active,
  spot,
  onChoose,
  onActivate,
  elementRef,
  registerRow,
  multiple = false,
  chosen,
  query = '',
  searchable = false,
  footerAction,
}: SelectListboxProps) {
  const shown = items.map((item) => selectMatches(item, query))
  const anyShown = shown.some(Boolean)
  const footerIndex = items.length
  let lastGroup: string | undefined

  return (
    <div
      ref={elementRef}
      style={spot as CSSProperties}
      className={cn(
        'fixed z-popover flex flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-lg',
        // The same settle every other panel uses. The app already flattens this
        // under a reduced-motion setting, and the class says so as well so the
        // panel does not depend on a rule in a stylesheet to stay still.
        'animate-scale-in motion-reduce:animate-none',
      )}
    >
      {searchable ? (
        /*
         * The search row is drawn, not focused. Focus stays on the trigger for
         * the whole interaction, as the header of this file explains, and the
         * trigger hands typed letters to this row. A real field in the body
         * would take focus out of whatever dialog the select sits in.
         */
        <div className="shrink-0 border-b border-line p-1" onMouseDown={(event) => event.preventDefault()}>
          <Input
            inputSize="sm"
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            value={query}
            placeholder="Search"
            leading={<IconSearch />}
            className="shadow-none"
          />
        </div>
      ) : null}
      <ul
        role="listbox"
        id={id}
        aria-labelledby={labelledBy}
        aria-multiselectable={multiple ? true : undefined}
        className="min-h-0 flex-1 overflow-y-auto p-1"
      >
        {items.length === 0 ? (
          <li className="px-2.5 py-2 text-base text-ink-subtle">Nothing to choose from yet</li>
        ) : null}
        {items.length > 0 && !anyShown ? (
          <li className="p-4 text-center text-base text-ink-subtle">No results found</li>
        ) : null}
        {items.map((item, index) => {
          if (shown[index] !== true) return null
          const isDisabled = item.disabled === true
          const isChosen = multiple ? chosen?.has(index) === true : index === selected
          const heading = item.group !== undefined && item.group !== lastGroup ? item.group : undefined
          lastGroup = item.group
          const supporting = isDisabled && item.disabledReason ? item.disabledReason : item.description
          return [
            heading !== undefined ? (
              <li
                key={`group-${heading}-${index}`}
                role="presentation"
                className="px-2.5 py-2 text-xs font-semibold text-ink-subtle"
              >
                {heading}
              </li>
            ) : null,
            <li
              key={`${item.value}-${index}`}
              id={selectOptionId(id, index)}
              role="option"
              aria-selected={isChosen}
              /*
               * `aria-disabled` and not a removed row. A row that states its
               * precondition instead of vanishing is the house reading, the
               * same one `MenuPanel` is written to.
               */
              aria-disabled={isDisabled ? true : undefined}
              title={isDisabled ? item.disabledReason : undefined}
              ref={(node) => registerRow?.(index, node)}
              // Keep the pointer from taking focus off the trigger, which is
              // where the whole keyboard contract lives.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                if (!isDisabled) onChoose?.(index)
              }}
              onMouseEnter={() => {
                if (!isDisabled) onActivate?.(index)
              }}
              className={cn(
                'flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-base transition',
                isDisabled && 'cursor-not-allowed text-ink-faint',
                !isDisabled && index === active && 'bg-surface-hover',
              )}
            >
              {multiple ? <RowCheck checked={isChosen} /> : null}
              {item.icon ? (
                <span className="inline-flex shrink-0 text-ink-subtle [&>svg]:h-5 [&>svg]:w-5">{item.icon}</span>
              ) : null}
              <span className="min-w-0 flex-1 truncate">
                <span className={cn('font-medium', !isDisabled && 'text-ink')}>{item.label}</span>
                {supporting ? (
                  <span className={cn('ml-1', isDisabled ? 'text-ink-faint' : 'text-ink-subtle')}>{supporting}</span>
                ) : null}
              </span>
              {item.count !== undefined ? (
                <span data-numeric="" className="shrink-0 tabular-nums text-ink-subtle">
                  {item.count}
                </span>
              ) : null}
              {!multiple && isChosen ? <IconCheck className="h-5 w-5 shrink-0 text-primary-600" /> : null}
            </li>,
          ]
        })}
      </ul>
      {footerAction ? (
        <div className="shrink-0 border-t border-line p-1">
          <button
            type="button"
            tabIndex={-1}
            id={selectOptionId(id, footerIndex)}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => onActivate?.(footerIndex)}
            onClick={() => onChoose?.(footerIndex)}
            className={cn(
              'flex h-9 w-full items-center gap-2 rounded-md px-2.5 text-base font-semibold text-primary-700 transition',
              'hover:bg-surface-hover [&>svg]:h-5 [&>svg]:w-5',
              active === footerIndex && 'bg-surface-hover',
            )}
          >
            {footerAction.icon}
            {footerAction.label}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** The native options, kept in step with the painted ones. */
function syncNativeMultiple(node: HTMLSelectElement, next: readonly string[]): void {
  for (const option of Array.from(node.options)) option.selected = next.includes(option.value)
  node.dispatchEvent(new Event('change', { bubbles: true }))
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    className,
    invalid,
    options,
    placeholder,
    selectSize = 'md',
    variant = 'outline',
    id,
    children,
    onChange,
    onFocus,
    autoFocus,
    leading,
    searchable,
    footerAction,
    optionDescription,
    multiple = false,
    values,
    defaultValues,
    onValuesChange,
    ...rest
  },
  ref,
) {
  const field = useFieldContext()
  const isInvalid = invalid ?? field?.invalid ?? false
  const isDisabled = rest.disabled ?? field?.disabled ?? false
  const height = selectSize === 'sm' ? 'h-9 pl-3 pr-2.5' : 'h-10 pl-3.5 pr-3'

  const generatedId = useId()
  const triggerId = id ?? field?.id ?? `${generatedId}-select`
  const nativeId = `${triggerId}-control`
  const listboxId = `${triggerId}-listbox`

  // A multi-select's placeholder is what the trigger says when nothing is
  // ticked, never a row of its own: ticking "All campaigns" would mean nothing.
  const items = selectItemsFrom(options, children, multiple ? undefined : placeholder)
  const itemsKey = items.map((item) => item.value).join('\u0000')
  const isSearchable = searchable ?? items.length > SELECT_SEARCH_AT
  const footerIndex = items.length

  const nativeRef = useRef<HTMLSelectElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<(HTMLLIElement | null)[]>([])
  // Typed letters do not paint anything, so they are not state.
  const typed = useRef({ text: '', at: 0 })

  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const [query, setQuery] = useState('')
  const [spot, setSpot] = useState<MenuSpot | null>(null)
  const [ownValue, setOwnValue] = useState(() => initialSelectValue(rest.defaultValue, items))
  const [ownValues, setOwnValues] = useState<readonly string[]>(() => defaultValues ?? [])

  // A call site that passes `value` owns the value, exactly as it does with a
  // bare `<select>`: React snaps the element back when the handler declines the
  // change, and reading the prop rather than the element is how the painted
  // half stays on the same answer.
  const controlled = rest.value !== undefined
  const current = controlled ? selectValueOf(rest.value) : ownValue
  const selected = multiple ? -1 : items.findIndex((item) => item.value === current)
  const currentValues = values ?? ownValues
  const chosen = new Set<number>()
  if (multiple) items.forEach((item, index) => currentValues.includes(item.value) && chosen.add(index))

  // What the keys walk: the rows the search left visible, then the footer row.
  const walkable: SelectOption[] = searchableItems(items, isSearchable ? query : '')
  if (footerAction) walkable.push({ value: '\u0000footer', label: '' })

  const setNative = (node: HTMLSelectElement | null): void => {
    nativeRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref !== null && ref !== undefined) ref.current = node
  }

  // What the browser settled on, which is only knowable once the options are in
  // the document: with no `defaultValue` a select takes its first enabled
  // option, and the list often arrives a request later than the control.
  useLayoutEffect(() => {
    const node = nativeRef.current
    if (node === null || controlled || multiple) return
    setOwnValue(node.value)
  }, [controlled, multiple, itemsKey])

  /*
   * `autoFocus` means the trigger, and it cannot be left to the handler below.
   * React implements the prop by calling `focus()` while it mounts the element,
   * and the button's ref is attached after the select's, so at that moment
   * there is nothing yet to hand the focus on to. Three dialogs open on a
   * select, and all three would have opened with the focus on a control nobody
   * can see.
   */
  useLayoutEffect(() => {
    if (autoFocus !== true) return
    triggerRef.current?.focus()
  }, [autoFocus])

  const close = useCallback((cause: MenuCloseCause) => {
    setOpen(false)
    setQuery('')
    if (returnsFocusToTrigger(cause)) triggerRef.current?.focus({ preventScroll: true })
  }, [])

  const openAt = (at: number): void => {
    if (isDisabled) return
    setActive(at)
    setOpen(true)
  }

  const choose = (index: number): void => {
    if (footerAction && index === footerIndex) {
      close('select')
      footerAction.onSelect()
      return
    }
    const node = nativeRef.current
    if (multiple) {
      const item = items[index]
      if (item === undefined || item.disabled === true) return
      const next = toggleSelectValue(items, currentValues, item.value)
      if (values === undefined) setOwnValues(next)
      if (node !== null) syncNativeMultiple(node, next)
      onValuesChange?.(next)
      // The list stays open, because ticking three rows is one task.
      return
    }
    if (node !== null) commitSelectValue(nativeTarget(node), items[index])
    close('select')
  }

  const extraHeight = (isSearchable ? SELECT_EXTRA_ROW : 0) + (footerAction ? SELECT_EXTRA_ROW : 0)

  // Before paint, so the panel is never shown at last time's coordinates.
  useLayoutEffect(() => {
    if (!open) {
      setSpot(null)
      return
    }
    const anchor = triggerRef.current?.getBoundingClientRect()
    if (anchor === undefined) return
    setSpot(
      placeSelect(anchor, { width: window.innerWidth, height: window.innerHeight }, items.length, extraHeight),
    )
    // `items.length` rather than the items: the panel is placed from how many
    // rows there are, and depending on the array itself would re-place on every
    // render, which sets state, which renders again.
  }, [open, items.length, extraHeight])

  // The highlight is not real focus, so the scrolling a browser does for free
  // when focus moves has to be asked for.
  useEffect(() => {
    if (!open || active < 0) return
    rowRefs.current[active]?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (panelRef.current?.contains(target) === true) return
      // The trigger toggles, so counting a click on it here would close the
      // list and let its own handler reopen it, which reads as stuck open.
      if (triggerRef.current?.contains(target) === true) return
      close('outside')
    }

    const onScroll = (event: Event): void => {
      // Capture, so a scroll inside a dialog or a table closes this and not
      // only a scroll of the page. The panel's own rows are the exception, or
      // the arrow keys would close the list while walking it.
      const target = event.target
      if (target instanceof Node && panelRef.current?.contains(target) === true) return
      close('scroll')
    }

    const onResize = (): void => close('resize')

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [open, close])

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.altKey || event.ctrlKey || event.metaKey) return
    const fresh = Date.now() - typed.current.at < SELECT_TYPEAHEAD_MS
    const action = selectKeyAction(
      event.key,
      {
        open,
        active: open ? active : selected,
        typed: fresh ? typed.current.text : '',
        searchable: isSearchable,
        query,
      },
      walkable,
    )
    if (action.kind === 'none') return

    if (action.kind !== 'type') typed.current = { text: '', at: 0 }

    if (action.kind === 'open') openAt(action.active)
    if (action.kind === 'active') setActive(action.active)
    if (action.kind === 'choose') choose(action.index)
    if (action.kind === 'search') {
      setQuery(action.query)
      // Keep the highlight on a row the search left standing, or move it to
      // the first one, so Enter always means something visible.
      const narrowed = searchableItems(items, action.query)
      if (narrowed[active]?.disabled === true || active < 0 || active >= items.length) {
        setActive(nextMenuIndex(narrowed, -1, 'ArrowDown'))
      }
    }
    if (action.kind === 'type') {
      typed.current = { text: action.typed, at: Date.now() }
      if (action.index >= 0) {
        // Typing with the list shut moves the value, the way a native select
        // does, rather than opening something nobody asked to see.
        if (open) setActive(action.index)
        else if (!multiple) choose(action.index)
      }
    }
    if (action.kind === 'close') {
      close(action.cause)
      // Tab has somewhere to be, so it is left alone. Escape is not: a select
      // inside a dialog would otherwise close the dialog behind it, since the
      // dialog listens for Escape on the document.
      if (action.cause === 'tab') return
      event.stopPropagation()
    }
    event.preventDefault()
  }

  const shown = items[selected]
  const chosenItems = multiple ? items.filter((_, index) => chosen.has(index)) : []
  const showsPlaceholder = multiple
    ? chosenItems.length === 0
    : placeholder !== undefined && (selected === -1 || selected === 0)
  const label = multiple ? multiSelectLabel(chosenItems, placeholder) : shown?.label ?? (placeholder ?? '')
  const description = optionDescription ? optionDescription(shown) : null
  const hasDescription = description !== null && description !== undefined && description !== false && description !== ''
  const describedBy = hasDescription ? `${triggerId}-description` : undefined

  // In a multi-select the native control holds an array, and `value` from the
  // caller means nothing there, so it is dropped rather than half-applied.
  const nativeProps = multiple
    ? { ...rest, value: [...currentValues], defaultValue: undefined, multiple: true }
    : rest

  const control = (
    <div className="relative">
      <select
        {...nativeProps}
        ref={setNative}
        id={nativeId}
        tabIndex={-1}
        aria-hidden="true"
        required={rest.required ?? field?.required}
        disabled={isDisabled}
        onChange={(event) => {
          if (multiple) setOwnValues(Array.from(event.currentTarget.selectedOptions, (option) => option.value))
          else setOwnValue(event.currentTarget.value)
          onChange?.(event)
        }}
        onFocus={(event) => {
          // A call site holding the ref and calling `focus()` means the visible
          // control, which is the button.
          triggerRef.current?.focus()
          onFocus?.(event)
        }}
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      >
        {placeholder !== undefined && !multiple ? <option value="">{placeholder}</option> : null}
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>

      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        id={triggerId}
        disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={
          open && active >= 0 ? selectOptionId(listboxId, active) : undefined
        }
        aria-describedby={[field?.describedBy, describedBy].filter(Boolean).join(' ') || undefined}
        aria-invalid={isInvalid || undefined}
        aria-required={(rest.required ?? field?.required) === true ? true : undefined}
        onClick={() => {
          if (open) close('select')
          else openAt(openingActive(walkable, selected))
        }}
        onKeyDown={onKeyDown}
        data-variant={variant === 'filled' ? 'filled' : undefined}
        className={cn(
          variant === 'filled' ? CONTROL_FILLED : CONTROL,
          isInvalid ? INVALID : variant === 'filled' ? VALID_FILLED : VALID,
          height,
          'flex items-center gap-2 py-0 text-left',
          className,
        )}
      >
        {leading ? (
          <span className="inline-flex shrink-0 text-ink-subtle [&>svg]:h-5 [&>svg]:w-5">{leading}</span>
        ) : null}
        <span className={cn('min-w-0 flex-1 truncate', showsPlaceholder ? 'text-ink-subtle' : 'font-medium')}>
          {label}
        </span>
        {/*
         * Inside the control rather than pinned to the wrapper. The wrapper is
         * a block and fills its parent, so a caller asking for `w-44` used to
         * leave the arrow stranded at the right of the row instead of at the
         * right of the control it belongs to.
         */}
        <IconChevronDown
          className={cn('h-5 w-5 shrink-0 text-ink-subtle transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && spot !== null
        ? createPortal(
            <SelectListbox
              id={listboxId}
              labelledBy={triggerId}
              items={items}
              selected={selected}
              active={active}
              spot={spot}
              onChoose={choose}
              onActivate={setActive}
              elementRef={panelRef}
              registerRow={(index, node) => {
                rowRefs.current[index] = node
              }}
              multiple={multiple}
              chosen={chosen}
              query={isSearchable ? query : ''}
              searchable={isSearchable}
              footerAction={footerAction}
            />,
            document.body,
          )
        : null}
    </div>
  )

  if (!optionDescription) return control

  return (
    <div>
      {control}
      {hasDescription ? (
        <p id={describedBy} className="mt-1.5 text-base text-ink-muted">
          {description}
        </p>
      ) : null}
    </div>
  )
})

/*
 * The three toggles live in their own files now, each with its own sizes and
 * states. They are re-exported here because a page that imported them from
 * this file before must not have to learn where they went.
 */
export { Checkbox, Radio } from './Checkbox'
export type { CheckboxProps, RadioProps } from './Checkbox'
export { Switch } from './Switch'
export type { SwitchProps } from './Switch'
